// zod schemas for anniversary forms (W3.1 §4.2 + BR-02/03/04).
//
// Mirrors backend `app/modules/anniversary/schemas.py` so client-side failures
// land before the round-trip. `reminder_days` enforces the locked subset
// {0,1,3,7,14,30} + ascending + length 0-6 (BR-03).

import { z } from "zod";

import { ANNIVERSARY_CATEGORIES } from "@/lib/anniversary/categoryIcon";
import {
  REMINDER_DAYS_ALLOWED,
  validateReminderDays,
} from "@/lib/anniversary/reminderDays";

export const anniversaryCategorySchema = z.enum(ANNIVERSARY_CATEGORIES as unknown as [
  string,
  ...string[],
]);

const anniversaryDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式為 YYYY-MM-DD")
  .refine((s) => {
    const d = new Date(s + "T00:00:00");
    const min = new Date("1900-01-01T00:00:00");
    const max = new Date();
    max.setFullYear(max.getFullYear() + 5);
    return d >= min && d <= max;
  }, "日期須在 1900-01-01 至 5 年後範圍內");

export const anniversaryReminderDaysSchema = z
  .array(z.number().int())
  .max(6, "提醒天數最多 6 個")
  .superRefine((arr, ctx) => {
    const r = validateReminderDays(arr);
    if (!r.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: r.message ?? "提醒天數校驗失敗",
      });
    }
  })
  .transform((arr) => {
    const filtered = arr.filter((v) => REMINDER_DAYS_ALLOWED.includes(v));
    return [...filtered].sort((a, b) => a - b);
  });

export const anniversaryReminderTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "提醒時間格式為 HH:MM")
  .nullable()
  .optional();

export const anniversaryCreateSchema = z
  .object({
    title: z
      .string()
      .min(1, "請輸入標題")
      .max(100, "標題最長 100 字"),
    description: z
      .string()
      .max(2000, "描述最長 2000 字")
      .nullable()
      .optional()
      .or(z.literal("")),
    date: anniversaryDateSchema,
    category: anniversaryCategorySchema,
    custom_category_label: z
      .string()
      .max(30, "自訂分類標籤最長 30 字")
      .nullable()
      .optional()
      .or(z.literal("")),
    is_recurring: z.boolean().default(true),
    reminder_days: anniversaryReminderDaysSchema.default([]),
    reminder_time: anniversaryReminderTimeSchema,
    is_private: z.boolean().default(false),
  })
  .superRefine((val, ctx) => {
    if (val.category === "custom" && !val.custom_category_label) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["custom_category_label"],
        message: "請輸入自訂分類標籤",
      });
    }
  });

export const anniversaryUpdateSchema = z.object({
  title: z.string().min(1).max(100).nullable().optional(),
  description: z.string().max(2000).nullable().optional().or(z.literal("")),
  date: anniversaryDateSchema.optional().nullable(),
  category: anniversaryCategorySchema.nullable().optional(),
  custom_category_label: z
    .string()
    .max(30)
    .nullable()
    .optional()
    .or(z.literal("")),
  is_recurring: z.boolean().nullable().optional(),
  reminder_days: anniversaryReminderDaysSchema.nullable().optional(),
  reminder_time: anniversaryReminderTimeSchema,
  is_private: z.boolean().nullable().optional(),
});

export const anniversaryIdSchema = z.object({
  id: z.string().uuid("紀念日 id 格式不正確"),
});

export type AnniversaryCreateFormInput = z.input<typeof anniversaryCreateSchema>;
export type AnniversaryCreateParsed = z.output<typeof anniversaryCreateSchema>;
export type AnniversaryUpdateFormInput = z.input<typeof anniversaryUpdateSchema>;
export type AnniversaryUpdateParsed = z.output<typeof anniversaryUpdateSchema>;